import { Select, Spin, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { posService } from '@/services/pos.service';
import type { PosStaff } from '../types';

interface Props {
  value?: string;
  onChange: (staffId: string) => void;
  storeId?: string;
  allowedStaffIds?: string[];
  placeholder?: string;
  warning?: boolean;
  error?: boolean;
  errorText?: string;
}

export function StaffAsyncSelect({
  value,
  onChange,
  storeId,
  allowedStaffIds,
  placeholder = 'Chọn nhân viên',
  warning = false,
  error = false,
  errorText,
}: Props) {
  const { data: allStaff = [], isLoading } = useQuery({
    queryKey: ['pos', 'staff', storeId],
    queryFn: () => posService.getStaffList(),
    staleTime: 2 * 60 * 1000,
  });

  const options: PosStaff[] =
    allowedStaffIds && allowedStaffIds.length > 0
      ? allStaff.filter((s) => allowedStaffIds.includes(s.staff_id))
      : allStaff;

  const noAllowedStaff = allowedStaffIds !== undefined && allowedStaffIds.length === 0;
  // Error (required-but-empty) takes precedence over warning (no warehouse staff).
  const status: 'error' | 'warning' | undefined = error
    ? 'error'
    : warning || noAllowedStaff
      ? 'warning'
      : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, width: '100%', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', minWidth: 0 }}>
        <Select
          style={{ flex: 1, minWidth: 0 }}
          placeholder={placeholder}
          value={value || undefined}
          onChange={onChange}
          loading={isLoading}
          notFoundContent={isLoading ? <Spin size="small" /> : 'Không có nhân viên'}
          options={options.map((s) => ({ value: s.staff_id, label: s.name }))}
          allowClear={false}
          status={status}
        />
        {!error && (warning || noAllowedStaff) && (
          <Tooltip title="Không có nhân viên kho phụ trách">
            <WarningOutlined style={{ color: '#faad14', fontSize: 12 }} />
          </Tooltip>
        )}
      </div>
      {error && errorText && (
        <span style={{ color: '#ff4d4f', fontSize: 11, lineHeight: 1.2 }}>{errorText}</span>
      )}
    </div>
  );
}
